import { useEffect, useState, useContext } from "react";
import EmployeeWorkDay from "../types/api/WorkDay";
import { Button, Checkbox, FormControl, FormControlLabel, Input, InputLabel, MenuItem, Paper, Select, Typography, useTheme } from "@mui/material";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { translateDayName } from "../tools/utils";
import Employee from "../types/api/Employee";
import { AppContext } from '../context/appContext';
import HourSelection from "../components/Inputs/HourSelection";
import { PREDEFINED_SHIFTS, PREDEFINED_SHIFTS_VALUES } from "../tools/constants";

interface WorkDayFormProps {
    workDay: EmployeeWorkDay,
    employee: Employee,
    interact: (status: boolean) => void,
    delete: (workDay: EmployeeWorkDay) => void,
    validate: (oldWorkday: EmployeeWorkDay, newWorkday: EmployeeWorkDay) => void
}

export function WorkDaySchedulesForm(workDayProps: WorkDayFormProps) {
    const theme = useTheme();

    const appContext = useContext(AppContext);
    const [workday, setWorkDay] = useState<EmployeeWorkDay>({...workDayProps.workDay});
    const [duration, setDuration] = useState(workday.start && workday.end? workday.end - workday.start:0);

    const selectedPredefinedShift = (Object.values(PREDEFINED_SHIFTS).find((shift) => shift === workday.shiftName) ?? PREDEFINED_SHIFTS.CUSTOM) as PREDEFINED_SHIFTS;

    const handlePredefinedShiftChange = (shiftType: PREDEFINED_SHIFTS) => {
        const shiftToOverrideWith = PREDEFINED_SHIFTS_VALUES[shiftType];
        setWorkDay({
            ...workday,
            ...shiftToOverrideWith,
        });
    };

    useEffect(()=> {
        let d = 0;
        if (workday.start && workday.end) {
            d += workday.end - workday.start;
        }
        if (workday.isDouble && workday.doubleStart && workday.doubleEnd) {
            d += workday.doubleEnd - workday.doubleStart;
        }

        if(workday.end === 25 || (workday.isDouble && workday.doubleEnd === 25)) {
            d -= 1;
        }
        setDuration(d);
    }, [workday])

    return (
        <div className="flex flex-col items-center justify-center h-full w-full p-8 rounded-lg">
            <div className="mb-4">
                <h1 className="text-2xl font-bold">Saisie manuelle des horaires</h1>
                <Typography className="italic text-xs">Modifiez les heures de travails manuellement</Typography>
            </div>

            <div className="flex flex-col mt-4 gap-4 w-full">
                <FormControl variant="standard">
                    <InputLabel htmlFor="workday-day"><CalendarTodayIcon/> - Jour</InputLabel>
                    <Input
                        id="formation-name"
                        defaultValue={translateDayName(workDayProps.workDay.Days)}
                        aria-describedby="workday-day"
                        disabled
                    />
                </FormControl>

                <FormControl variant="standard">
                    <InputLabel id="select-formation-label">Poste :</InputLabel>
                    <Select
                        multiple
                        labelId="select-formation-label"
                        id="select-formation"
                        value={workday.formations ? workday.formations.map(f => f.id) : []}
                        onChange={(data)=>{
                            const selectedIds = data.target.value as number[];
                            const selectedFormations = workDayProps.employee.formations.map((employeeFormation)=>employeeFormation.formation).filter(f => selectedIds.includes(f.id));
                            setWorkDay({...workday, formations: selectedFormations});
                        }}
                        label="Poste"
                    >
                        {workDayProps.employee.formations.map((formation) => (
                            <MenuItem key={formation.formation.id} value={formation.formation.id}>{formation.formation.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl variant="standard" sx={{ minWidth: 220 }}>
                    <InputLabel id="select-shift-type-label">Type de shift</InputLabel>
                    <Select
                        labelId="select-shift-type-label"
                        id="select-shift-type"
                        value={selectedPredefinedShift}
                        onChange={(data)=>handlePredefinedShiftChange(data.target.value as PREDEFINED_SHIFTS)}
                        label="Type de shift"
                    >
                        <MenuItem value={PREDEFINED_SHIFTS.CUSTOM}>Personnalisable</MenuItem>
                        <MenuItem value={PREDEFINED_SHIFTS.DAY}>Journée</MenuItem>
                        <MenuItem value={PREDEFINED_SHIFTS.EVENING}>Soirée</MenuItem>
                        <MenuItem value={PREDEFINED_SHIFTS.DOUBLE}>Double</MenuItem>
                    </Select>
                </FormControl>
                <div className="flex flex-row mt-4 gap-4 w-full justify-around">
                    <HourSelection 
                        label="Heure de début :"
                        selectedHour={workday.start}
                        setSelectedHour={(data)=>{setWorkDay({...workday, start: data.target.value as number})}}
                        min={8}
                        max={workday.end ? workday.end -1 : 24}
                        disabled={workday.shiftName != null}
                    />
                    <HourSelection
                        label="Heure de fin :"
                        selectedHour={workday.end}
                        setSelectedHour={(data)=>{setWorkDay({...workday, end: data.target.value as number})}}
                        min={workday.start ? workday.start + 3 : 8}
                        max={25}
                        disabled={workday.shiftName != null}
                    />
                </div>

                <div className="flex flex-row mt-4 px-4">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={workday.isDouble || false}
                                onChange={(e) => setWorkDay({ ...workday, isDouble: e.target.checked })}
                                disabled={workday.shiftName != null}
                            />
                        }
                        label="Double coupure"
                    />
                </div>

                {workday.isDouble && (
                    <div className="flex flex-row justify-around mt-4">
                        <HourSelection
                            label="Heure de début (2) :"
                            selectedHour={workday.doubleStart}
                            setSelectedHour={(data)=>{setWorkDay({...workday, doubleStart: data.target.value as number})}}
                            min={8}
                            max={24}
                            disabled={workday.shiftName != null}
                        />
                        <HourSelection
                            label="Heure de fin (2) :"
                            selectedHour={workday.doubleEnd}
                            setSelectedHour={(data)=>{setWorkDay({...workday, doubleEnd: data.target.value as number})}}
                            min={workday.doubleStart ? workday.doubleStart + 3 : 8}
                            max={25}
                            disabled={workday.shiftName != null}
                        />
                    </div>
                )}

                <Paper className="flex flex-row justify-between px-4 py-6" sx={{backgroundColor: theme.palette.background.paper}}>
                    <div>Durée totale :</div>
                    <div>{ duration } H</div>
                </Paper>

            </div>
            

            <div className="flex flex-row justify-between mt-8 w-full">
                {workday.start !== null || workday.end !== null ? (
                    <Button
                        variant="contained"
                        color="error"
                        onClick={()=>{
                            workDayProps.delete(workday);
                        }}
                    >
                        Supprimer
                    </Button>
                ) : null}
                <Button
                    variant="contained"
                    color="error"
                    onClick={()=>{
                        workDayProps.interact(false);
                    }}
                >
                    Fermer
                </Button>

                <Button
                    variant="contained"
                    color="success"
                    onClick={()=>{
                        if (!workday.formations || workday.formations.length <= 0) {
                            appContext.showSnackbar("Veuillez sélectionner un poste.", 'error');
                            return;
                        }
                        if( workday.start === null || workday.end === null ) {
                            appContext.showSnackbar("Veuillez sélectionner des horaires valides.", 'error');
                            return;
                        }
                        if (workday.isDouble && (workday.doubleStart === null || workday.doubleEnd === null)) {
                            appContext.showSnackbar("Veuillez sélectionner des horaires valides pour la double coupure.", 'error');
                            return;
                        }
                        workDayProps.validate(workDayProps.workDay, workday);
                        workDayProps.interact(false);
                    }}
                >
                    Valider
                </Button>
            </div>
            
        </div>
    )

}